import React, { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { Card, Container, Form } from "react-bootstrap";
import CommonButton from "../../components/commonButton";
import InputField from "../../components/form/InputField";
import CustomSelect from "../../components/form/CustomSelect";
import { ArrowLeftIcon } from "../../assets/icons";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import useMounted from "../../hooks/useMounted";
import {
  addNpaProperties,
  importBankUserList,
  propertyTypesDropdown,
  updateNpaProperties,
} from "../../redux/slice/propertiesSlice";
import { FormFeedback, FormLabel } from "../../components/form/utils";
import { formatMomentDate } from "../../utils/dateHelper";
import { Formik } from "formik";
import { addPropertySchema } from "../../schema/validationSchemas";
import FileInput from "../../components/fileInput";
import { routes } from "../../routes/pageRoutes";
import { useLoader } from "../../context/LoaderContext";
import { useToast } from "../../context/TostContext";
import { CircleAlert, User } from "lucide-react";

const AddNpaProperties = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const hasMounted = useMounted();
  const { setLoading } = useLoader();
  const {id} = useParams();
  const editValues = location?.state?.item;
  const isEdit = location?.state?.isEdit;
  const toast = useToast();
  const [addPropertyActiveTab, setAddPropertyActiveTab] = useState("property");
  const headerPortalTarget = document.getElementById("header-portal-target");
  const renderHeaderContent = () => (
    <div className="d-flex align-items-center justify-content-between w-100 pe-3">
      <div className="d-flex align-items-center gap-2">
        <Link
          to={'/npa-properties'}
          className="border-0 bg-transparent p-0 d-flex align-items-center justify-content-center primary"
        >
          <ArrowLeftIcon size={22} />
        </Link>
        <h4 className="fw-semibold mb-0 text-primary fs-lg">
          {isEdit ? "Edit Property" : "New Property"}
        </h4>
      </div>
    </div>
  );

  const [setImagePreview] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);

  const [dropdownData, setDropdownData] = useState({
    property_types: [],
    asset_categories: [],
    possession_types: [],
    auction_types: [],
  });

  const propertyTypeOptions = useMemo(() => {
    return dropdownData?.property_types?.map((item) => ({
      value: item.id,
      label: item.name,
    })) || [];
  }, [dropdownData?.property_types]);

  const assetCategoryOptions = useMemo(() => {
    return dropdownData?.asset_categories?.map((item) => ({
      value: item.id,
      label: item.name,
    })) || [];
  }, [dropdownData?.asset_categories]);

  const possessionTypeOptions = useMemo(() => {
    return dropdownData?.possession_types?.map((item) => ({
      value: item.id,
      label: item.name,
    })) || [];
  }, [dropdownData?.possession_types]);

  const auctionTypeOptions = useMemo(() => {
    return dropdownData?.auction_types?.map((item) => ({
      value: item.id,
      label: item.name,
    })) || [];
  }, [dropdownData?.auction_types]);


  const initialEventState = {
    assigned_to: editValues?.bank_user_id || "",
    bank_id: editValues?.bank_user?.bank_id || "",
    branch_id: editValues?.bank_user?.branch_id || "",
    asset_category: editValues?.asset_category_id || "",
    property_type: editValues?.property_type_id || "",
    property_address: editValues?.address_line_1 || "",
    province_state: editValues?.state || "",
    taluka: editValues?.taluka || "",
    city_town: editValues?.city || "",
    area_locality: editValues?.area_locality || "",
    land_area: editValues?.land_area || "",
    builtup_carpet_area: editValues?.carpet_area || "",
    borrower_names: editValues?.borrower_names || "",
    possession_type: editValues?.possession_type_id || "",
    reserve_price: editValues?.reserve_price || "",
    emd: editValues?.emd || "",
    min_increment: editValues?.min_increment || "",
    amount_outstanding: editValues?.amount_outstanding || "",
    dsc_required: editValues?.dsc_required || "",
    auction_type: editValues?.auction_type_id || "",
    auction_start_time: editValues?.auction_start_time || "",
    auction_end_time: editValues?.auction_end_time || "",
    time_extension: editValues?.time_extension || "",
    application_submission_date: editValues?.application_submission_date || "",
    property_inspection_datetime:
      editValues?.property_inspection_date || "",
    service_provider: editValues?.service_provider || "",
    description: editValues?.description || "",
    country: editValues?.country || "",
    property_document: editValues?.property_document?.url || "",
    contact_name: editValues?.contact_name || "",
    contact_number: editValues?.contact_number || "",
  };


  useEffect(() => {
    dispatch(propertyTypesDropdown()).then((res) => {
      if (res?.payload?.success) {
        setDropdownData(res?.payload?.data);
      }
    });

  }, [dispatch]);


  const handleSubmit = async (values, { setSubmitting }) => {
    const formData = new FormData();
    formData.append("bank_user_id", values?.assigned_to);
    formData.append("asset_category_id", values.asset_category);
    formData.append("property_type_id", values.property_type);
    formData.append("address_line_1", values.property_address);
    formData.append("state", values?.province_state);
    formData.append("taluka", values?.taluka);
    formData.append("city", values.city_town);
    formData.append("area_locality", values.area_locality);
    formData.append("land_area", values.land_area);
    formData.append("carpet_area", values?.builtup_carpet_area);
    formData.append("borrower_names", values?.borrower_names);
    formData.append("possession_type_id", values.possession_type);
    formData.append("reserve_price", values.reserve_price);
    formData.append("emd", values.emd);
    formData.append("min_increment", values?.min_increment);
    formData.append("amount_outstanding", values?.amount_outstanding);
    formData.append("dsc_required", values?.dsc_required);
    formData.append("auction_type_id", values?.auction_type);
    formData.append("auction_start_time", values.auction_start_time);
    formData.append("auction_end_time", values.auction_end_time);
    formData.append("time_extension", values.time_extension);
    formData.append(
      "application_submission_date",
      values.application_submission_date
    );
    formData.append(
      "property_inspection_date",
      values.property_inspection_datetime
    );
    formData.append("service_provider", values.service_provider);
    formData.append("description", values?.description);
    formData.append("country", values?.country);
    formData.append("contact_name", values?.contact_name);
    formData.append("contact_number", values?.contact_number);

    // ✅ append file ONLY if it's a real File object
    if (values.property_document instanceof File) {
      formData.append("file", values.property_document);
    }

    setLoading(true);
    try {
      const result = isEdit
        ? await dispatch(updateNpaProperties({ id, data: formData })).unwrap()
        : await dispatch(addNpaProperties({ data: formData })).unwrap();


      toast.success(
        result?.msg ||
        (isEdit
          ? "Property updated successfully!"
          : "Property added successfully!")
      );

      setTimeout(() => {
        if (hasMounted) {
          navigate(routes.npaProperties, { replace: true });
        }
      }, 1000);
    } catch (err) {
      toast.error(
        err?.message ||
        (isEdit
          ? "Failed to update property!"
          : "Failed to add property!")
      );
      setSubmitting(false);
      setLoading(false);
    } finally {
      // setShowToast(true);
      setSubmitting(false);
      setLoading(false);
    }
  };

  const handleImagePdfChange = (type, file, setFieldValue) => {
    if (type === "image") {
      if (file) {
        setFieldValue("property_image", file);
        setImagePreview(URL.createObjectURL(file));
      }
    } else if (type === "pdf") {
      if (file) {
        setFieldValue("property_document", file);
        setPdfPreview(URL.createObjectURL(file));
      }
    }
  };

  const handleNumericInput = (e, setFieldValue, fieldName) => {
    const value = e.target.value;
    const numericValue = value.replace(/[^0-9.]/g, "");
    const parts = numericValue.split(".");
    const sanitizedValue =
      parts.length > 2
        ? `${parts[0]}.${parts.slice(1).join("")}`
        : numericValue;

    setFieldValue(fieldName, sanitizedValue);
  };

  return (
    <>
      {headerPortalTarget && createPortal(renderHeaderContent(), headerPortalTarget)}

      <Container className={headerPortalTarget ? "mt-4" : ""}>

        <Formik
          initialValues={{
            ...initialEventState,
            dsc_required: isEdit ? initialEventState.dsc_required || false : "",
          }}
          validationSchema={addPropertySchema}
          onSubmit={handleSubmit}
        >
          {({
            errors,
            touched,
            isSubmitting,
            handleChange,
            values,
            setFieldValue,
            handleSubmit,
          }) => (
            <Form onSubmit={handleSubmit}>
              <Card className="bg-white border-0 p-4 rounded-2 shadow-sm">
                <div className="d-flex align-items-center gap-3">
                  <CommonButton
                    type="button"
                    icon={<User className={addPropertyActiveTab ? "text-white" : "text-secondary"} />}
                    variant={addPropertyActiveTab === "property" ? "primary" : "outline"}
                    className="gap-2"
                    title="Property"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAddPropertyActiveTab("property");
                    }}
                  />
                  <CommonButton
                    type="button"
                    icon={<CircleAlert />}
                    variant={addPropertyActiveTab === "bank-details" ? "primary" : "outline"}
                    title="Bank Details"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAddPropertyActiveTab("bank-details");
                    }}
                    disabled={
                      !values?.borrower_names ||
                      !values?.property_type ||
                      !values?.asset_category ||
                      !values?.land_area ||
                      !values?.builtup_carpet_area ||
                      !values?.possession_type ||
                      !values?.auction_start_time ||
                      !values?.auction_end_time ||
                      !values?.property_inspection_datetime ||
                      !values?.auction_type ||
                      !values?.application_submission_date ||
                      !values?.time_extension ||
                      !values?.property_address ||
                      !values?.area_locality ||
                      !values?.taluka ||
                      !values?.city_town ||
                      !values?.province_state ||
                      !values?.country ||
                      !values?.service_provider ||
                      !values?.description
                    }
                  />
                </div>
                {addPropertyActiveTab === "property" ? (
                  <div className="mt-4 pt-1 grid gap-4">
                    <InputField
                      className="g-col-6 g-col-xxl-4"
                      label={{ name: "Borrower Name" }}
                      input={{
                        id: "borrower_names",
                        autoFocus: true,
                        name: "borrower_names",
                        type: "text",
                        placeholder: "Elenor.Aguilar",
                        value: values?.borrower_names,
                        onChange: (e) => {
                          setFieldValue("borrower_names", e.target.value);
                        },
                      }}
                      validation={{
                        isInvalid: touched.borrower_names && errors.borrower_names,
                        message: errors.borrower_names,
                      }}
                    />

                    <CustomSelect
                      label={{ name: "Property Type" }}
                      name="property_type"
                      value={values.property_type}
                      onChange={(selectedOption) => {
                        setFieldValue(
                          "property_type",
                          selectedOption ? selectedOption.value : ""
                        );
                      }}
                      options={propertyTypeOptions}
                      placeholder="Select a property type"
                      validation={{
                        isInvalid: touched.property_type && errors.property_type,
                        message: errors.property_type,
                      }}
                      isClearable={true}
                      className="g-col-6 g-col-xxl-4"
                    />

                    <CustomSelect
                      label={{ name: "Asset Category" }}
                      name="asset_category"
                      value={values.asset_category}
                      onChange={(selectedOption) => {
                        setFieldValue(
                          "asset_category",
                          selectedOption ? selectedOption.value : ""
                        );
                      }}
                      options={assetCategoryOptions}
                      placeholder="Select a asset category"
                      validation={{
                        isInvalid: touched.asset_category && errors.asset_category,
                        message: errors.asset_category,
                      }}
                      isClearable={true}
                      className="g-col-6 g-col-xxl-4"
                    />
                    <InputField
                      className="g-col-6 g-col-xxl-4"
                      label={{ name: "Land Area" }}
                      input={{
                        id: "land_area",
                        name: "land_area",
                        type: "text",
                        value: values?.land_area,
                        placeholder: "5000.50",
                        onChange: (e) => handleNumericInput(e, setFieldValue, "land_area"),
                      }}
                      validation={{
                        isInvalid: touched.land_area && errors.land_area,
                        message: errors.land_area,
                      }}
                    />
                    <InputField
                      className="g-col-6 g-col-xxl-4"
                      label={{ name: "Carpet Area" }}
                      input={{
                        id: "builtup_carpet_area",
                        name: "builtup_carpet_area",
                        type: "text",
                        value: values?.builtup_carpet_area,
                        placeholder: "3500.25",
                        onChange: (e) => handleNumericInput(e, setFieldValue, "builtup_carpet_area"),
                      }}
                      validation={{
                        isInvalid: touched.builtup_carpet_area && errors.builtup_carpet_area,
                        message: errors.builtup_carpet_area,
                      }}
                    />
                    <div className="g-col-6 g-col-xxl-4">
                      <div className="mt-21">
                        <FormLabel labelName="DSC Required" />
                        <div className="d-flex align-items-center justify-content-start h-100 gap-4">
                          <div className="d-flex align-items-center gap-2">
                            <Form.Check
                              type="radio"
                              name="dsc_required"
                              label="Yes"
                              value={true}
                              checked={values.dsc_required === true}
                              onChange={(e) =>
                                setFieldValue("dsc_required", true)}
                            />
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <Form.Check
                              type="radio"
                              name="dsc_required"
                              label="No"
                              value={false}
                              checked={values.dsc_required === false}
                              onChange={(e) =>
                                setFieldValue("dsc_required", false)
                              }
                            />
                          </div>
                        </div>
                        {touched.dsc_required && errors.dsc_required ? (
                          <FormFeedback message={errors.dsc_required} />
                        ) : null}
                      </div>
                    </div>
                    <CustomSelect
                      label={{ name: "Possession Type" }}
                      name="possession_type"
                      value={values.possession_type}
                      onChange={(selectedOption) => {
                        setFieldValue(
                          "possession_type",
                          selectedOption ? selectedOption.value : ""
                        );
                      }}
                      options={possessionTypeOptions}
                      placeholder="Select a possession type"
                      validation={{
                        isInvalid: touched.possession_type && errors.possession_type,
                        message: errors.possession_type,
                      }}
                      isClearable={true}
                      className="g-col-6 g-col-xxl-4"
                    />
                    <InputField
                      className="g-col-3 g-col-xxl-2"
                      label={{ name: "Auction Start Date" }}
                      input={{
                        id: "auction_start_time",
                        name: "auction_start_time",
                        type: "datetime-local",
                        placeholder: "Start date",
                        value: formatMomentDate(values?.auction_start_time, "YYYY-MM-DDTHH:mm"),
                        onChange: (e) => {
                          setFieldValue("auction_start_time", e.target.value);
                        },
                      }}
                      validation={{
                        isInvalid: touched.auction_start_time && errors.auction_start_time,
                        message: errors.auction_start_time,
                      }}
                    />
                    <InputField
                      className="g-col-3 g-col-xxl-2"
                      label={{ name: "Auction End Date" }}
                      input={{
                        id: "auction_end_time",
                        name: "auction_end_time",
                        type: "datetime-local",
                        placeholder: "End Date",
                        value: formatMomentDate(values?.auction_end_time, "YYYY-MM-DDTHH:mm"),
                        onChange: (e) => {
                          setFieldValue("auction_end_time", e.target.value);
                        },
                        disabled: !values?.auction_start_time,
                      }}
                      validation={{
                        isInvalid: touched.auction_end_time && errors.auction_end_time,
                        message: errors.auction_end_time,
                      }}
                    />
                    <InputField
                      className="g-col-6 g-col-xxl-4"
                      label={{ name: "Property Inspection date" }}
                      input={{
                        id: "property_inspection_datetime",
                        name: "property_inspection_datetime",
                        type: "datetime-local",
                        placeholder: "Start date",
                        value: formatMomentDate(values?.property_inspection_datetime, "YYYY-MM-DDTHH:mm"),
                        onChange: (e) => {
                          setFieldValue("property_inspection_datetime", e.target.value);
                        },
                      }}
                      validation={{
                        isInvalid: touched.property_inspection_datetime && errors.property_inspection_datetime,
                        message: errors.property_inspection_datetime,
                      }}
                    />
                    <CustomSelect
                      label={{ name: "Auction Type" }}
                      name="auction_type"
                      value={values.auction_type}
                      onChange={(selectedOption) => {
                        setFieldValue("auction_type", selectedOption ? selectedOption.value : "");
                      }}
                      options={auctionTypeOptions}
                      placeholder="Select a Auction Type"
                      validation={{
                        isInvalid: touched.auction_type && errors.auction_type,
                        message: errors.auction_type,
                      }}
                      isClearable={true}
                      className="g-col-6 g-col-xxl-4"
                    />
                    <InputField
                      className="g-col-6 g-col-xxl-4"
                      label={{ name: "Application Submission date & time" }}
                      input={{
                        id: "application_submission_date",
                        name: "application_submission_date",
                        type: "datetime-local",
                        placeholder: "Start date",
                        value: formatMomentDate(values?.application_submission_date, "YYYY-MM-DDTHH:mm"),
                        onChange: (e) => {
                          setFieldValue("application_submission_date", e.target.value);
                        },
                      }}
                      validation={{
                        isInvalid: touched.application_submission_date && errors.application_submission_date,
                        message: errors.application_submission_date,
                      }}
                    />
                    <InputField
                      className="g-col-6 g-col-xxl-4"
                      label={{ name: "Time Extension" }}
                      input={{
                        id: "time_extension",
                        name: "time_extension",
                        type: "time",
                        placeholder: "Select time",
                        value: values?.time_extension,
                        onChange: (e) => {
                          setFieldValue("time_extension", e.target.value);
                        },
                      }}
                      validation={{
                        isInvalid: touched.time_extension && errors.time_extension,
                        message: errors.time_extension,
                      }}
                    />
                    <InputField
                      className="g-col-6 g-col-xxl-4"
                      label={{ name: "Address LIne 1" }}
                      input={{
                        id: "property_address",
                        name: "property_address",
                        type: "text",
                        placeholder: "123, Palm Beach Road, Sector 15, Navi Mumbai",
                        value: values.property_address,
                        onChange: handleChange,
                      }}
                      validation={{
                        isInvalid: touched.property_address && errors.property_address,
                        message: errors.property_address,
                      }}
                    />
                    <InputField
                      className="g-col-6 g-col-xxl-4"
                      label={{ name: "Area Locality" }}
                      input={{
                        id: "area_locality",
                        name: "area_locality",
                        type: "text",
                        placeholder: "Sector 15",
                        value: values?.area_locality,
                        onChange: handleChange,
                      }}
                      validation={{
                        isInvalid: touched.area_locality && errors.area_locality,
                        message: errors.area_locality,
                      }}
                    />
                    <InputField
                      className="g-col-6 g-col-xxl-4"
                      label={{ name: "Taluka" }}
                      input={{
                        id: "taluka",
                        name: "taluka",
                        type: "text",
                        placeholder: "Thane",
                        value: values?.taluka,
                        onChange: handleChange,
                      }}
                      validation={{
                        isInvalid: touched.taluka && errors.taluka,
                        message: errors.taluka,
                      }}
                    />
                    <InputField
                      className="g-col-6 g-col-xxl-4"
                      label={{ name: "City" }}
                      input={{
                        id: "city_town",
                        name: "city_town",
                        type: "text",
                        placeholder: "City",
                        value: values?.city_town,
                        onChange: handleChange,
                      }}
                      validation={{
                        isInvalid: touched.city_town && errors.city_town,
                        message: errors.city_town,
                      }}
                    />
                    <InputField
                      className="g-col-6 g-col-xxl-4"
                      label={{ name: "State" }}
                      input={{
                        id: "province_state",
                        name: "province_state",
                        type: "text",
                        placeholder: "State",
                        value: values.province_state,
                        onChange: handleChange,
                      }}
                      validation={{
                        isInvalid: touched.province_state && errors.province_state,
                        message: errors.province_state,
                      }}
                    />
                    <InputField
                      className="g-col-6 g-col-xxl-4"
                      label={{ name: "Country" }}
                      input={{
                        id: "country",
                        name: "country",
                        type: "text",
                        placeholder: "Country",
                        value: values?.country,
                        onChange: handleChange,
                      }}
                      validation={{
                        isInvalid: touched.country && errors.country,
                        message: errors.country,
                      }}
                    />
                    <InputField
                      className="g-col-6 g-col-xxl-4"
                      label={{ name: "Service Provider" }}
                      input={{
                        id: "service_provider",
                        name: "service_provider",
                        type: "text",
                        placeholder: "AuctionCorp Pvt. Ltd.",
                        value: values?.service_provider,
                        onChange: handleChange,
                      }}
                      validation={{
                        isInvalid: touched.service_provider && errors.service_provider,
                        message: errors.service_provider,
                      }}
                    />
                    <InputField
                      className="g-col-12"
                      label={{ name: "Description" }}
                      input={{
                        id: "description",
                        name: "description",
                        as: "textarea",
                        rows: 3,
                        placeholder: "Description",
                        value: values?.description,
                        onChange: handleChange,
                      }}
                      validation={{
                        isInvalid: touched.description && errors.description,
                        message: errors.description,
                      }}
                    />

                    <div className="g-col-12">
                      <div className="d-flex align-content-center gap-3">
                        <CommonButton
                          type="button"
                          title="Next"
                          variant="outline-primary"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setAddPropertyActiveTab("bank-details");
                          }}
                          disabled={
                            !values?.borrower_names ||
                            !values?.property_type ||
                            !values?.asset_category ||
                            !values?.land_area ||
                            !values?.builtup_carpet_area ||
                            !values?.possession_type ||
                            !values?.auction_start_time ||
                            !values?.auction_end_time ||
                            !values?.property_inspection_datetime ||
                            !values?.auction_type ||
                            !values?.application_submission_date ||
                            !values?.time_extension ||
                            !values?.property_address ||
                            !values?.area_locality ||
                            !values?.taluka ||
                            !values?.city_town ||
                            !values?.province_state ||
                            !values?.country ||
                            !values?.service_provider ||
                            !values?.description
                          }
                        />

                        <CommonButton
                          title="Cancel"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(routes.npaProperties);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 pt-1 grid gap-4">
                    <CustomSelect
                      label={{ name: "Select a Bank User" }}
                      name="assigned_to"
                      value={values.assigned_to}
                      onChange={(name, val) => setFieldValue("assigned_to", val || "")}
                      fetchOptions={async (search, page) => {
                        const response = await dispatch(importBankUserList({ search, page, limit: 10 })).unwrap();
                        return { data: response.data, meta: { page: response.pagination.page, pages: response.pagination.total_pages } };
                      }}
                      dataMapper={(data) => data.map((item) => ({ value: item.id, label: item.name }))}
                      initialLabel={editValues?.bank_user?.name || ""}
                      selectProps={{ placeholder: "Select a Bank User" }}
                      validation={{
                        isInvalid: errors?.assigned_to && touched?.assigned_to,
                        message: errors?.assigned_to,
                      }}
                      className="g-col-6 g-col-xxl-4"
                    />
                    
                    <InputField
                      className="g-col-6 g-col-xxl-4"
                      label={{ name: "EMD" }}
                      input={{
                        id: "emd",
                        name: "emd",
                        type: "text",
                        placeholder: "250000.00",
                        value: values?.emd,
                        onChange: handleChange,
                      }}
                      validation={{
                        isInvalid: touched.emd && errors.emd,
                        message: errors.emd,
                      }}
                    />
                    <InputField
                      className="g-col-6 g-col-xxl-4"
                      label={{ name: "Reserve Price" }}
                      input={{
                        id: "reserve_price",
                        name: "reserve_price",
                        type: "text",
                        placeholder: "25000000.00",
                        value: values?.reserve_price,
                        onChange: handleChange,
                      }}
                      validation={{
                        isInvalid: touched.reserve_price && errors.reserve_price,
                        message: errors.reserve_price,
                      }}
                    />
                    <InputField
                      className="g-col-6 g-col-xxl-4"
                      label={{ name: "Minimum Increment" }}
                      input={{
                        id: "min_increment",
                        name: "min_increment",
                        type: "text",
                        placeholder: "50000.00",
                        value: values?.min_increment,
                        onChange: handleChange,
                      }}
                      validation={{
                        isInvalid: touched.min_increment && errors.min_increment,
                        message: errors.min_increment,
                      }}
                    />
                    <InputField
                      className="g-col-6 g-col-xxl-4"
                      label={{ name: "Amount Outstanding" }}
                      input={{
                        id: "amount_outstanding",
                        name: "amount_outstanding",
                        type: "text",
                        placeholder: "15000000.00",
                        value: values?.amount_outstanding,
                        onChange: handleChange,
                      }}
                      validation={{
                        isInvalid: touched.amount_outstanding && errors.amount_outstanding,
                        message: errors.amount_outstanding,
                      }}
                    />
                    <div className="g-col-12">
                      <div className="d-flex align-items-center mb-2">
                        <User className="text-secondary contact-detail-img" />
                        <h5 className="mb-0 fs-lg">Contact Details</h5>
                      </div>
                      <div className="grid gap-4">
                        <InputField
                          className="g-col-6 g-col-xxl-4"
                          label={{ name: "Contact Name" }}
                          input={{
                            id: "contact_name",
                            name: "contact_name",
                            type: "text",
                            placeholder: "Name",
                            value: values?.contact_name,
                            onChange: handleChange,
                          }}
                          validation={{
                            isInvalid: touched.contact_name && errors.contact_name,
                            message: errors.contact_name,
                          }}
                        />
                        <InputField
                          className="g-col-6 g-col-xxl-4"
                          label={{ name: "Contact Number" }}
                          input={{
                            id: "contact_number",
                            name: "contact_number",
                            type: "text",
                            placeholder: "Contact Number",
                            value: values?.contact_number,
                            onChange: handleChange,
                          }}
                          validation={{
                            isInvalid: touched.contact_number && errors.contact_number,
                            message: errors.contact_number,
                          }}
                        />
                      </div>
                    </div>
                    <div className="g-col-12">
                      <div className="d-flex align-items-center mb-2">
                        <User className="text-secondary contact-detail-img" />
                        <h5 className="mb-0 fs-lg">Upload Documents</h5>
                      </div>
                      <div className="grid gap-4">
                        <div md={6} className="g-col-6 g-col-xxl-4">
                          <div>
                            <FormLabel
                              id="property_document"
                              labelName="Upload Pdf"
                            />
                            <FileInput
                              name="property_document"
                              accept=".pdf"
                              value={values?.property_document}
                              onChange={(file) =>
                                handleImagePdfChange(
                                  "pdf",
                                  file,
                                  setFieldValue
                                )
                              }
                              error={errors?.property_document}
                              touched={touched.property_document}
                            />
                            {touched.property_document &&
                              errors?.property_document && (
                                <FormFeedback message={errors?.property_document} />
                              )}
                            {pdfPreview && (
                              <div className="mt-3">
                                <a
                                  href={pdfPreview}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  View Uploaded PDF
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                        <div md={6} className="g-col-6 g-col-xxl-4">
                          {isEdit &&
                            (values.property_document || pdfPreview) && (
                              <>
                                <div className="mt-3">
                                  {typeof values.property_document ===
                                    "string" &&
                                    values.property_document.includes(
                                      ".pdf"
                                    ) && (
                                      <a
                                        href={`${values.property_document}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        {values.property_document
                                          .split("property_document-")
                                          .pop()}
                                      </a>
                                    )}
                                </div>
                                <div
                                  style={{
                                    width: "100%",
                                    height: "400px",
                                    border: "1px solid #ddd",
                                    borderRadius: "5px",
                                    overflow: "hidden",
                                  }}
                                >
                                  <iframe
                                    src={
                                      pdfPreview ||
                                      (typeof values.property_document ===
                                        "string"
                                        ? `${values.property_document}`
                                        : "")
                                    }
                                    title="PDF Viewer"
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      border: "none",
                                    }}
                                  ></iframe>
                                </div>
                              </>
                            )}
                        </div>
                        <div className="g-col-12">
                          <div className="d-flex align-items-center gap-3">
                            <CommonButton
                              title="Submit"
                              type="submit"
                              variant="primary"
                              disabled={isSubmitting}
                            />
                            <CommonButton
                              type="button"
                              title="Cancel"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(routes.npaProperties);
                              }}
                            />
                            <CommonButton
                              type="button"
                              title="Back"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                setAddPropertyActiveTab("property");
                              }}
                            />
                            {touched.property_document &&
                              errors?.property_document && (
                                <FormFeedback message={errors?.property_document} />
                              )}
                            {pdfPreview && (
                              <div className="mt-3">
                                <a
                                  href={pdfPreview}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  View Uploaded PDF
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            </Form>
          )
          }
        </Formik>
      </Container>
    </>
  );
};

export default AddNpaProperties;
